/**
 * Guard Rejection Telemetry — P-3 (Audit Verbeterplan v4.1)
 *
 * Persists pedagogical guard rejection statistics so teachers and admins
 * can observe what the guard is filtering and why.
 *
 * Data is stored per-session and aggregated for reporting.
 * Uses the dataLayer for persistence (localStorage backend).
 *
 * ARCHITECTURE: This is a write-only telemetry sink during practice.
 * Admin/teacher portals read via getGuardTelemetry().
 */

import type { GuardStats, GuardRejection } from './pedagogicalGuard'
import { debug } from '../../utils/logger'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface GuardTelemetryEntry {
  /** ISO timestamp of the session */
  timestamp: string
  /** Learner account ID */
  learnerId: string
  /** Module being practiced */
  moduleId: string
  /** Session type: 'practice' | 'assessment' | 'fluency' */
  sessionType: 'practice' | 'assessment' | 'fluency'
  /** Guard stats from filterSession */
  stats: GuardStats
  /** P-9: Per-question rejection details (denormalized from stats for fast lookup) */
  rejections?: GuardRejection[]
}

export interface GuardTelemetrySummary {
  /** Total sessions recorded */
  totalSessions: number
  /** Total questions evaluated */
  totalQuestions: number
  /** Total fatal rejections */
  totalFatalRejections: number
  /** Total soft failures */
  totalSoftFailures: number
  /** Total successful replacements */
  totalReplacements: number
  /** Rejection rate (fatal / total) */
  fatalRejectionRate: number
  /** Replacement success rate (replaced / fatal) */
  replacementSuccessRate: number
  /** Top rejection reasons with counts */
  topReasons: { reason: string; count: number }[]
  /** Recent entries (last 50) */
  recentEntries: GuardTelemetryEntry[]
}

// ─── Storage Key ──────────────────────────────────────────────────────────────

const TELEMETRY_KEY = 'djay_guard_telemetry'
const MAX_ENTRIES = 200 // Rolling window

// ─── Core API ─────────────────────────────────────────────────────────────────

/**
 * Record guard stats from a completed session.
 * Called by filterSession after evaluation.
 */
export function recordGuardTelemetry(
  learnerId: string,
  moduleId: string,
  sessionType: 'practice' | 'assessment' | 'fluency',
  stats: GuardStats,
): void {
  try {
    const entries = loadEntries()
    const entry: GuardTelemetryEntry = {
      timestamp: new Date().toISOString(),
      learnerId,
      moduleId,
      sessionType,
      stats,
      rejections: stats.rejections?.length ? stats.rejections : undefined,
    }
    entries.push(entry)

    // Trim to rolling window
    const trimmed = entries.slice(-MAX_ENTRIES)
    localStorage.setItem(TELEMETRY_KEY, JSON.stringify(trimmed))

    debug(
      `[GuardTelemetry] Recorded: ${stats.total} evaluated, ${stats.fatalRejected} fatal, ${stats.softFailed} soft, ${stats.replaced} replaced`,
    )
  } catch (e) {
    // Telemetry is non-critical — never break the app
    debug(`[GuardTelemetry] Failed to record: ${e}`)
  }
}

/**
 * Get aggregated telemetry summary for admin/teacher portal.
 * Optionally filter by learnerId.
 */
export function getGuardTelemetry(learnerId?: string): GuardTelemetrySummary {
  const allEntries = loadEntries()
  const entries = learnerId
    ? allEntries.filter((e) => e.learnerId === learnerId)
    : allEntries

  if (entries.length === 0) {
    return {
      totalSessions: 0,
      totalQuestions: 0,
      totalFatalRejections: 0,
      totalSoftFailures: 0,
      totalReplacements: 0,
      fatalRejectionRate: 0,
      replacementSuccessRate: 0,
      topReasons: [],
      recentEntries: [],
    }
  }

  let totalQuestions = 0
  let totalFatal = 0
  let totalSoft = 0
  let totalReplaced = 0
  const reasonCounts: Record<string, number> = {}

  for (const entry of entries) {
    totalQuestions += entry.stats.total
    totalFatal += entry.stats.fatalRejected
    totalSoft += entry.stats.softFailed
    totalReplaced += entry.stats.replaced
    for (const [reason, count] of Object.entries(entry.stats.reasons)) {
      reasonCounts[reason] = (reasonCounts[reason] ?? 0) + count
    }
  }

  const topReasons = Object.entries(reasonCounts)
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count)

  return {
    totalSessions: entries.length,
    totalQuestions,
    totalFatalRejections: totalFatal,
    totalSoftFailures: totalSoft,
    totalReplacements: totalReplaced,
    fatalRejectionRate: totalQuestions > 0 ? totalFatal / totalQuestions : 0,
    replacementSuccessRate: totalFatal > 0 ? totalReplaced / totalFatal : 0,
    topReasons,
    recentEntries: entries.slice(-50),
  }
}

/**
 * Clear all telemetry data (admin action).
 */
export function clearGuardTelemetry(): void {
  localStorage.removeItem(TELEMETRY_KEY)
  debug('[GuardTelemetry] Cleared all telemetry data')
}

// ─── Internal ─────────────────────────────────────────────────────────────────

function loadEntries(): GuardTelemetryEntry[] {
  try {
    const raw = localStorage.getItem(TELEMETRY_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
  } catch {
    return []
  }
}
